import React, { useCallback, useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import ageVerifIcon from '../../assets/age-verif.svg';
import nakedNewsLogoWhite from '../../assets/Affiliate/Logos Icons/NN-logo.png';
import { getAgeVerificationProviderUrl } from '../../services/getAgeVerificationProviderUrl';
import { doLogout } from '../../utils/auth';

import styles from './AgeVerificationOverlay.module.css';


const vmaStyle = {
  height: 720,
  width: 480,
  left: () => (window.screen.width / 2) - (vmaStyle.height / 2),
  top: () => (window.screen.height / 2) - (vmaStyle.width / 2),
};

const YOTI_CALLBACK_PATH = '/yoti/verify';
const PING_INTERVAL = 5000;

function AgeVerificationOverlay(props) {
  const { user } = props;
  const popupRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const [ageVerificationUrl, setAgeVerificationUrl] = useState(null);

  const onMessage = useCallback((event) => {
    const { data, source } = event;
    const { pathname: eventPath, origin: eventOrigin } = source.location;
    const { origin } = window.location;

    if (eventOrigin !== origin) {
      return;
    }

    if (eventPath !== YOTI_CALLBACK_PATH) {
      return;
    }

    window.removeEventListener('message', onMessage);
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (data.verified) {
      window.location.reload();
    }
  }, []);

  const startPopupPing = useCallback((popup) => {
    pingIntervalRef.current = setInterval(() => {
      if (popup.closed) {
        clearInterval(pingIntervalRef.current);

        return;
      }

      try {
        const targetOrigin = `${window.location.origin}/yoti/verify`;
        popup.postMessage({ type: 'ping' }, targetOrigin);
      } catch (error) {
        // Expected during external verification
      }
    }, PING_INTERVAL);
  }, []);

  useEffect(() => {
    const loadVerificationUrl = async() => {
      try {
        const params = { redirect_uri: window.location.origin };
        const { data } = await getAgeVerificationProviderUrl(params);
        const url = data?.ageVerificationUrl;
        setAgeVerificationUrl(url);
      } catch (error) {
        // eslint-disable-next-line no-alert
        alert(`Failed to preload verification URL: ${error.message}`);
      }
    };

    loadVerificationUrl();
  }, []);

  const handleVerifyMyAge = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      return;
    }

    if (!ageVerificationUrl) {
      // eslint-disable-next-line no-alert
      alert('Verification URL not ready. Please try again in a moment.');

      return;
    }

    try {
      window.addEventListener('message', onMessage, false);

      const styleConfigs = [
        'resizable=no',
        `height=${vmaStyle.height}`,
        `width=${vmaStyle.width}`,
        `left=${vmaStyle.left()}`,
        `top=${vmaStyle.top()}`,
      ].join(',');

      popupRef.current = window.open(ageVerificationUrl, '', styleConfigs);

      if (popupRef.current) {
        startPopupPing(popupRef.current);
      } else {
        window.removeEventListener('message', onMessage);
        // eslint-disable-next-line no-alert
        alert('Please allow popups for age verification to work properly.');
      }
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('Failed to start age verification. Please try again.');
    }
  }, [ageVerificationUrl, onMessage, startPopupPing]);

  useEffect(() => () => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    window.removeEventListener('message', onMessage);
  }, [onMessage]);

  const handleLogin = useCallback(() => {
    window.location.href = '/account/login';
  }, []);

  const handleSignOut = useCallback((event) => {
    event.preventDefault();
    doLogout();
  }, []);

  const renderButtons = () => (
    <div className={styles.buttonsContainer}>
      <button
        className={styles.verifyButton}
        onClick={handleVerifyMyAge}
      >
        Verify My Age
      </button>
      {!user?.authenticated && (
        <button
          className={styles.loginButton}
          onClick={handleLogin}
        >
          Login
        </button>
      )}
      {user?.authenticated && (
        <button
          className={styles.signOutButton}
          onClick={handleSignOut}
        >
          Sign Out
        </button>
      )}
    </div>
  );

  const renderContent = () => (
    <div className={styles.content}>
      As per state law, please verify your age. It takes just 30 seconds and we do not store personal data.
    </div>
  );

  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
        <div className={styles.imageContainer}>
          <img
            alt="Age Verification Required"
            className={classnames(styles.ageVerifIcon, 'unblurred')}
            src={ageVerifIcon}
          />
        </div>
        {renderContent()}
        {renderButtons()}
        <div
          className={classnames(styles.ageVerifRedBackground, { [styles.nnLogo]: true })}
        />
        <div className={styles.logoContainer}>
          <img
            alt="Naked News: The Program With Nothing To Hide"
            className={classnames(styles.logo, 'unblurred')}
            src={nakedNewsLogoWhite}
          />
        </div>
      </div>
    </div>
  );
}

AgeVerificationOverlay.propTypes = { user: PropTypes.object };

AgeVerificationOverlay.defaultProps = { user: null };

export default observer(AgeVerificationOverlay);
